import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Tabs } from '@factorialco/f0-react/dist/experimental'
import { compact } from 'lodash'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { NodeHeader } from 'modules/job_catalog/components/NodeHeader'
import CompetenciesAttributes from 'modules/job_catalog/components/TreeNodeAttributes/CompetenciesAttributes'
import OverviewAttributes from 'modules/job_catalog/components/TreeNodeAttributes/OverviewAttributes'
import WorkingConditionsAttributes from 'modules/job_catalog/components/TreeNodeAttributes/WorkingConditionsAttributes'
import { useJobCatalogTreeVisibility } from 'modules/job_catalog/hooks/useJobCatalogTreeVisibility'
import { useTreeNode } from 'modules/job_catalog/hooks/useTreeNode'
import {
  CategoryType,
  DEFAULT_CATEGORIES,
  NODE_TYPES,
  getCategoryTranslation,
  getParentChain,
  maximizeClickPath,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'

const TreeNodePage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id } = useParams<{ id: string }>()

  const { canSeeCompetencies } = useJobCatalogTreeVisibility()

  const [activeTab, setActiveTab] = useState<CategoryType | null>(null)

  const {
    nodeId,
    nodeName,
    nodeType,
    currentNode,
    currentAttributes,
    salaries,
    childrenInformation,
  } = useTreeNode(id)

  const tabs = useMemo(
    () =>
      compact([
        normalizeNodeType(nodeType) !== NODE_TYPES.LEVEL && DEFAULT_CATEGORIES.OVERVIEW,
        canSeeCompetencies && DEFAULT_CATEGORIES.COMPETENCIES,
      ]),
    [nodeType, canSeeCompetencies]
  )

  useEffect(() => {
    const firstTab = tabs[0]
    if (firstTab) setActiveTab(firstTab)
  }, [tabs])

  if (!nodeId || !nodeType || !currentNode) return null

  const handleTabChange = (tabId: string) => {
    const newTab = tabs.find((tab) => tab === tabId)
    if (newTab) setActiveTab(newTab)
  }

  const handleMaximizeClick = () => {
    if (!currentNode || !id) return

    navigate(
      maximizeClickPath(
        getParentChain(currentNode),
        normalizeNodeType(currentNode.type),
        id,
        navigation,
        currentNode.type
      )
    )
  }

  return (
    <Sidepanel
      onClose={() => navigate(navigation.employees.jobCatalog.overview.fullPath())}
      open
      width="wide"
      title={nodeName}
      boxPadding={false}
      options={[
        {
          title: i18n.t('common.edit'),
          icon: 'pencil',
          onClick: handleMaximizeClick,
        },
      ]}
    >
      <NodeHeader
        name={currentNode.name}
        description={currentNode.description || ''}
        type={currentNode.type}
        childrens={currentNode.children}
        salaries={salaries}
        canEditRole={false}
        currentNode={currentNode}
        isMaximized
      />
      <Box gap="s16" height="full-view-height">
        {tabs.length > 0 && (
          <Tabs
            tabs={tabs.map((value) => ({
              label: getCategoryTranslation(value, normalizeNodeType(nodeType)),
              id: value,
            }))}
            setActiveTabId={handleTabChange}
            activeTabId={activeTab ?? undefined}
          />
        )}
        <Box height="full">
          {activeTab === DEFAULT_CATEGORIES.COMPETENCIES && (
            <CompetenciesAttributes
              items={currentAttributes.filter((item) => item.type === activeTab)}
            />
          )}
          {activeTab === DEFAULT_CATEGORIES.WORKING_CONDITIONS && (
            <WorkingConditionsAttributes
              items={currentAttributes.filter((item) => item.type === activeTab)}
            />
          )}
          {activeTab === DEFAULT_CATEGORIES.OVERVIEW && (
            <OverviewAttributes
              currentNode={currentNode}
              childrenInformation={childrenInformation}
            />
          )}
        </Box>
      </Box>
    </Sidepanel>
  )
}

export default TreeNodePage
